/**
 * Converte um recorte (formato nativo do react-easy-crop: x/y/width/height
 * em % da imagem original) em estilos CSS puros, aplicados a um <img>
 * position:absolute dentro de um container overflow:hidden.
 *
 * Pura matemática — sem canvas — para rodar idêntico no preview (iframe) e
 * na captura via Playwright (mesmo motor de renderização em ambos os casos).
 * Ver DOCS/00-visao-geral.md e o plano de implementação, seção "Cropper".
 */
export interface CropAreaPercent {
  x: number
  y: number
  width: number
  height: number
  /** Rotação não destrutiva, em graus. Ausente nos slides antigos. */
  rotation?: number
  /** Filtro preto e branco não destrutivo. Ausente = foto colorida. */
  grayscale?: boolean
  /** Proporção natural do arquivo (largura/altura), necessária para girar sem deformar. */
  sourceAspect?: number
}

function effectsStyle(crop: CropAreaPercent | undefined | null, elementAspect: number): string {
  const rotation = Number(crop?.rotation ?? 0)
  const grayscale = crop?.grayscale ? 'filter:grayscale(1);' : ''
  if (!rotation) return grayscale

  // Ao girar um retângulo, amplia o elemento apenas o necessário para que os
  // cantos da máscara continuem cobertos (inclusive a 90° em formatos altos).
  const radians = Math.abs(rotation % 180) * Math.PI / 180
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))
  const safeAspect = elementAspect > 0 ? elementAspect : 1
  const coverScale = Math.max(cos + sin / safeAspect, cos + sin * safeAspect)
  return `${grayscale}transform:rotate(${rotation}deg) scale(${coverScale});transform-origin:center;`
}

function inferSourceAspect(crop: CropAreaPercent, containerAspect: number, radians: number): number {
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))
  const bboxAspect = containerAspect * (crop.height / crop.width)
  const denominator = bboxAspect * sin - cos

  if (Math.abs(denominator) > 0.0001) {
    const inferred = (sin - bboxAspect * cos) / denominator
    if (Number.isFinite(inferred) && inferred > 0) return inferred
  }

  return containerAspect
}

function rotatedCropStyle(crop: CropAreaPercent, containerAspect: number, rotation: number): string {
  const radians = rotation * Math.PI / 180
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))
  const sourceAspect = crop.sourceAspect && crop.sourceAspect > 0
    ? crop.sourceAspect
    : inferSourceAspect(crop, containerAspect, radians)

  // O react-easy-crop devolve x/y/width/height sobre a caixa delimitadora
  // da imagem já girada. Reconstruímos primeiro essa caixa e, a partir da
  // proporção natural, calculamos as dimensões do arquivo antes da rotação.
  // Assim width e height nunca são forçados para proporções diferentes.
  const bboxWidthUnits = containerAspect * (100 / crop.width)
  const bboxHeightUnits = 100 / crop.height
  const heightFromWidth = bboxWidthUnits / (cos * sourceAspect + sin)
  const heightFromHeight = bboxHeightUnits / (sin * sourceAspect + cos)
  const imageHeightUnits = (heightFromWidth + heightFromHeight) / 2
  const imageWidthPercent = (imageHeightUnits * sourceAspect / containerAspect) * 100
  const imageHeightPercent = imageHeightUnits * 100
  const bboxWidthPercent = (100 / crop.width) * 100
  const bboxHeightPercent = (100 / crop.height) * 100
  const bboxLeftPercent = -(crop.x / crop.width) * 100
  const bboxTopPercent = -(crop.y / crop.height) * 100
  const overdraw = 0.6
  const left = bboxLeftPercent + (bboxWidthPercent - imageWidthPercent) / 2 - overdraw / 2
  const top = bboxTopPercent + (bboxHeightPercent - imageHeightPercent) / 2 - overdraw / 2
  const grayscale = crop.grayscale ? 'filter:grayscale(1);' : ''

  return `position:absolute;left:${left}%;top:${top}%;width:${imageWidthPercent + overdraw}%;height:${imageHeightPercent + overdraw}%;max-width:none;${grayscale}transform:rotate(${rotation}deg);transform-origin:center;`
}

export function cropToImgStyle(crop: CropAreaPercent | undefined | null, containerAspect = 1): string {
  if (!crop || crop.width <= 0 || crop.height <= 0) {
    return `position:absolute;inset:-1px;width:calc(100% + 2px);height:calc(100% + 2px);object-fit:cover;${effectsStyle(crop, containerAspect)}`
  }
  const rotation = Number(crop.rotation ?? 0)
  if (rotation) return rotatedCropStyle(crop, containerAspect, rotation)

  const overdraw = 0.6
  const width = 100 / crop.width * 100 + overdraw
  const height = 100 / crop.height * 100 + overdraw
  const left = -(crop.x / crop.width) * 100 - overdraw / 2
  const top = -(crop.y / crop.height) * 100 - overdraw / 2
  const elementAspect = containerAspect * (width / height)
  return `position:absolute;left:${left}%;top:${top}%;width:${width}%;height:${height}%;max-width:none;${effectsStyle(crop, elementAspect)}`
}
